import { BlobContent } from './BlobContent';
import { DiffContent, Entity, TrackingBuilder } from "../base";
import { Context } from "../base";
import { assertType, uuid } from "../base/utils";
import { CachingRef, IdentityRef, Ref } from './Ref';

class ExternalFile implements Entity<ExternalFile> {
  static STORE = "ExternalFile";
  private _uuid: string;
  private _name: string;
  private _type: string;
  private _blob: Ref<BlobContent>;

  constructor(uuid: string, name: string, type: string, blob: Ref<BlobContent>) {
    assertType(uuid, "uuid", String);
    assertType(name, "name", String);
    assertType(type, "type", String);
    assertType(blob, "blob", Ref);
    this._uuid = uuid;
    this._name = name;
    this._type = type;
    this._blob = blob;
  }

  get uuid(): string { return this._uuid; }
  get name(): string { return this._name; }
  get type(): string { return this._type; }
  get blob(): Ref<BlobContent> { return this._blob; }

  async getFile() : Promise<File> {
    const urlEncoded = this._blob.obj().content;
    const blob = await (await fetch(urlEncoded)).blob();
    return new File([blob], this._name,{type: this._type});
  }

  static async fromFile(file: File, id?: string) : Promise<ExternalFile> {
    const fileId = id || uuid(); 
    return new Promise<ExternalFile>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (function() {
        const dataUrl = reader.result;
        if (typeof dataUrl == "string") {
          const blobContent = new BlobContent(uuid(), "", dataUrl);
          const externalfile = new ExternalFile(fileId, file.name, file.type, blobContent.getCachingRef());
          resolve(externalfile);
        }
        reject("illegal state");
      });
      reader.readAsDataURL(file);
    });
  }

  getRef() {
    return new Ref(ExternalFile, this._uuid);
  }

  getCachingRef() {
    return CachingRef.of(ExternalFile, this._uuid, this);
  }

  builder(): ExternalFileBuilder {
    return new ExternalFileBuilder(this);
  }

  toJson(): ExternalFileJSON {
    return {
      uuid: this._name,
      name: this._name,
      type: this._type,
      blob: this._blob.uuid
    };
  }

  static identityRef = new IdentityRef(ExternalFile);
  static identityValue = new ExternalFile(ExternalFile.identityRef.uuid, "", "", BlobContent.identityRef);
  static identity(): ExternalFile {
    return ExternalFile.identityValue;
  }

  static fromUuid(uuid: string): ExternalFile | undefined {
    const finder = Context.getInstance().getFinder(ExternalFile);
    return finder.findById(uuid);
  }

  static createRef(uuid: string): Ref<ExternalFile> {
    return CachingRef.of(ExternalFile, uuid);
  }

  static fromJson(externalfile: ExternalFileJSON): ExternalFile {
    return new ExternalFile(externalfile.uuid, externalfile.name, externalfile.type, BlobContent.createRef(externalfile.blob));
  }
}

class ExternalFileBuilder extends TrackingBuilder<ExternalFile> {
  private _uuid: string;
  private _name: string;
  private _type: string;
  private _blob: Ref<BlobContent>;
  private _diff: ExternalFileDiff = {};

  constructor(object: ExternalFile | undefined) {
    super();
    const obj = object ? object : ExternalFile.identity();
    this._uuid = obj.uuid;
    this._name = obj.name;
    this._type = obj.type;
    this._blob = obj.blob;
  }

  uuid(uuid: string) {
    if (this.isTracking() && this._uuid !== uuid) {
      this._diff.uuid = uuid;
    }
    this._uuid = uuid;
    return this;
  }

  name(name: string) {
    if (this.isTracking() && this._name !== name) {
      this._diff.name = name;
    }
    this._name = name;
    return this;
  }

  type(type: string) {
    if (this.isTracking() && this._type !== type) {
      this._diff.type = type;
    }
    this._type = type;
    return this;
  }

  blob(blob: string | Ref<BlobContent>) {
    const blobRef = (typeof blob === "string") ? BlobContent.createRef(blob) : blob;
    if (this.isTracking() && this._blob.uuid !== blobRef.uuid) {
      this._diff.blob = blobRef.uuid;
    }
    this._blob = blobRef;
    return this;
  }

  build(): ExternalFile {
    return new ExternalFile(this._uuid, this._name, this._type, this._blob);
  }

  applyDiff(diff: ExternalFileDiff): ExternalFileBuilder {
    if (diff.uuid) {
      this.uuid(diff.uuid);
    }
    if (diff.name) {
      this.name(diff.name);
    }
    if (diff.type) {
      this.type(diff.type);
    }
    if (diff.blob) {
      this.blob(diff.blob);
    }
    return this;
  }

  applyObj(object: ExternalFile): ExternalFileBuilder {
    this.uuid(object.uuid);
    this.name(object.name);
    this.type(object.type);
    this.blob(object.blob);
    return this;
  }

  getDiff(): ExternalFileDiff {
    return this._diff;
  }
}

interface ExternalFileJSON {
  uuid: string,
  name: string,
  type: string,
  blob: string,
}

interface ExternalFileDiff extends DiffContent<ExternalFile> {
  uuid?: string,
  name?: string,
  type?: string,
  blob?: string,
}

export { ExternalFile, ExternalFileJSON, ExternalFileDiff, ExternalFileBuilder }